import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authenticatedFetch } from "../../utils/api";

const machineSchema = z.object({
  dia: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Diameter is required" }))
    .refine((val) => val > 0, "Diameter must be greater than 0"),
  gauge: z.enum(["14", "16", "24"], {
    required_error: "Gauge is required",
  }),
  machine_type: z.enum(["rib", "fine"], {
    required_error: "Type is required",
  }),
  count: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Count is required" }))
    .refine((val) => val >= 1, "Count must be at least 1"),
});

type MachineFormData = z.infer<typeof machineSchema>;

export default function CreateMachine() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");

  const { control, handleSubmit } = useForm<MachineFormData>({
    resolver: zodResolver(machineSchema),
    defaultValues: {
      dia: "" as any,
      gauge: "" as any,
      machine_type: "rib",
      count: 1,
    },
  });

  const onSubmit = async (data: MachineFormData) => {
    try {
      const response = await authenticatedFetch(`/machines`, {
        method: "POST",
        body: JSON.stringify({ machine: data }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      navigate("/machines");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h2">
              Create Machine
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/machines")}
              variant="outlined"
            >
              Back
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="dia"
              control={control}
              render={({
                field: { onChange, value, ...field },
                fieldState,
              }) => (
                <TextField
                  {...field}
                  value={value || ""}
                  onChange={(e) =>
                    onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                  label="Diameter"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="gauge"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <FormLabel>Gauge</FormLabel>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="14"
                      control={<Radio />}
                      label="14 GG"
                    />
                    <FormControlLabel
                      value="16"
                      control={<Radio />}
                      label="16 GG"
                    />
                    <FormControlLabel
                      value="24"
                      control={<Radio />}
                      label="24 GG"
                    />
                  </RadioGroup>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="machine_type"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <FormLabel>Type</FormLabel>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="rib"
                      control={<Radio />}
                      label="Rib"
                    />
                    <FormControlLabel
                      value="fine"
                      control={<Radio />}
                      label="Fine"
                    />
                  </RadioGroup>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="count"
              control={control}
              render={({
                field: { onChange, value, ...field },
                fieldState,
              }) => (
                <TextField
                  {...field}
                  value={value || ""}
                  onChange={(e) =>
                    onChange(e.target.value ? Number(e.target.value) : undefined)
                  }
                  label="Count (Number of Machines)"
                  type="number"
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || "Number of machines with this configuration"}
                  inputProps={{ min: 1 }}
                />
              )}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" fullWidth>
                Create Machine
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/machines")}
                fullWidth
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

